import React from 'react';
import { ArrowRight, Zap, CheckCircle, Shield, Clock, Lock } from 'lucide-react';
import { WatermarkDemoCard, WatermarkHighlight } from './WatermarkDemoCard';
import { useAuth } from '../AuthProvider';
import type { Locale } from '../../types/locale';

interface JumpLink {
  label: string;
  targetId: string;
}

interface TrustBadge {
  icon: 'shield' | 'clock' | 'lock' | 'check';
  label: string;
}

interface PSEOHeroProps {
  // H1 and subtitle - UNIQUE per page
  title: string;
  subtitle: string;

  // Trust badges - can be customized per page
  trustBadges?: TrustBadge[];

  // Jump links - UNIQUE per page based on sections
  jumpLinks?: JumpLink[];

  // CTA configuration
  ctaText: string;
  ctaTargetId: string;

  // Demo card content - UNIQUE per page
  demoCard: {
    heading: string;
    headingLabel: string;
    sampleTextBefore: React.ReactNode;
    sampleTextAfter: string;
    detectedCount: number;
    detectedLabel: string;
    cleanedLabel: string;
    stats: {
      stat1: { value: string; label: string };
      stat2: { value: string; label: string };
      stat3: { value: string; label: string };
    };
  };

  // Localization
  locale: Locale;
}

const iconMap = {
  shield: Shield,
  clock: Clock,
  lock: Lock,
  check: CheckCircle,
};

// Full 8-locale translations for pSEO components
// Each locale uses natural, SEO-optimized phrasing - NOT direct translations
const heroTranslations: Record<Locale, {
  signInWithGoogle: string;
  defaultTrustBadges: {
    worksInstantly: string;
    noDataStored: string;
    freeToUse: string;
  };
}> = {
  en: {
    signInWithGoogle: 'Sign in with Google',
    defaultTrustBadges: {
      worksInstantly: 'Works Instantly',
      noDataStored: 'No Data Stored',
      freeToUse: 'Free to Use',
    },
  },
  de: {
    signInWithGoogle: 'Mit Google anmelden',
    defaultTrustBadges: {
      worksInstantly: 'Sofort einsatzbereit',
      noDataStored: 'Keine Datenspeicherung',
      freeToUse: 'Kostenlos nutzbar',
    },
  },
  'pt-BR': {
    signInWithGoogle: 'Entrar com Google',
    defaultTrustBadges: {
      worksInstantly: 'Funciona na hora',
      noDataStored: 'Sem armazenamento',
      freeToUse: 'Totalmente gratis',
    },
  },
  'ms-MY': {
    signInWithGoogle: 'Log masuk dengan Google',
    defaultTrustBadges: {
      worksInstantly: 'Berfungsi serta-merta',
      noDataStored: 'Tiada data disimpan',
      freeToUse: 'Percuma untuk guna',
    },
  },
  es: {
    signInWithGoogle: 'Iniciar sesion con Google',
    defaultTrustBadges: {
      worksInstantly: 'Resultados al instante',
      noDataStored: 'Sin almacenamiento',
      freeToUse: 'Uso gratuito',
    },
  },
  fr: {
    signInWithGoogle: 'Se connecter avec Google',
    defaultTrustBadges: {
      worksInstantly: 'Traitement instantane',
      noDataStored: 'Aucune donnee stockee',
      freeToUse: 'Entierement gratuit',
    },
  },
  ja: {
    signInWithGoogle: 'Googleでログイン',
    defaultTrustBadges: {
      worksInstantly: '即時処理',
      noDataStored: 'データ保存なし',
      freeToUse: '完全無料',
    },
  },
  id: {
    signInWithGoogle: 'Masuk dengan Google',
    defaultTrustBadges: {
      worksInstantly: 'Proses instan',
      noDataStored: 'Tanpa penyimpanan data',
      freeToUse: 'Gratis digunakan',
    },
  },
};

// Helper function to get default trust badges for a locale
const getDefaultTrustBadges = (locale: Locale): TrustBadge[] => {
  const t = heroTranslations[locale] || heroTranslations.en;
  return [
    { icon: 'clock', label: t.defaultTrustBadges.worksInstantly },
    { icon: 'lock', label: t.defaultTrustBadges.noDataStored },
    { icon: 'shield', label: t.defaultTrustBadges.freeToUse },
  ];
};

/**
 * PSEOHero - Question-based hero section for pSEO pages
 *
 * Features:
 * - H1 optimized for target keyword question
 * - Direct answer in subtitle
 * - Jump links for navigation
 * - WatermarkDemoCard for visual proof
 */
export const PSEOHero: React.FC<PSEOHeroProps> = ({
  title,
  subtitle,
  trustBadges,
  jumpLinks = [],
  ctaText,
  ctaTargetId,
  demoCard,
  locale,
}) => {
  const { user, signIn } = useAuth();
  const t = heroTranslations[locale] || heroTranslations.en;

  // Use provided trust badges or fall back to locale-aware defaults
  const resolvedTrustBadges = trustBadges ?? getDefaultTrustBadges(locale);

  const handleCTA = () => {
    requestAnimationFrame(() => {
      const element = document.getElementById(ctaTargetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  const handleJumpLink = (targetId: string) => {
    requestAnimationFrame(() => {
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  // Localized sign in text from translation object
  const signInText = t.signInWithGoogle;

  return (
    <section className="relative overflow-hidden py-12 md:py-16 lg:py-20">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column - Content */}
          <div className="space-y-6 text-center lg:text-left">
            {/* Trust Badges */}
            {resolvedTrustBadges.length > 0 && (
              <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                {resolvedTrustBadges.map((badge, index) => {
                  const IconComponent = iconMap[badge.icon];
                  return (
                    <div
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-full text-sm font-medium border border-green-200 dark:border-green-800"
                    >
                      <IconComponent size={14} />
                      <span>{badge.label}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* H1 Title - Primary Keyword Question */}
            <h1 className="font-bold text-neutral-900 dark:text-white leading-tight">
              <span className="block text-3xl md:text-4xl lg:text-5xl">
                {title}
              </span>
            </h1>

            {/* Subtitle - Direct Answer */}
            <p className="text-lg md:text-xl text-neutral-600 dark:text-neutral-400 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              {subtitle}
            </p>

            {/* Jump Links */}
            {jumpLinks.length > 0 && (
              <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                {jumpLinks.map((link, index) => (
                  <button
                    key={index}
                    onClick={() => handleJumpLink(link.targetId)}
                    className="px-4 py-2 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start pt-2">
              <button
                onClick={handleCTA}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent-600 hover:bg-accent-700 text-white rounded-lg font-semibold text-lg shadow-sm hover:shadow transition-[background-color,box-shadow] duration-200"
              >
                <Zap size={20} className="flex-shrink-0" />
                <span className="whitespace-nowrap">{ctaText}</span>
                <ArrowRight size={20} className="flex-shrink-0" />
              </button>

              {/* Sign in button - hidden when user is logged in */}
              <button
                onClick={() => signIn()}
                className={`inline-flex items-center justify-center gap-2 px-8 py-4 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white rounded-lg font-semibold text-lg border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700 ${user ? 'invisible' : ''}`}
                aria-hidden={!!user}
                tabIndex={user ? -1 : 0}
              >
                <img
                  src="/google-icon.svg"
                  alt="Google"
                  width="20"
                  height="20"
                  loading="lazy"
                  decoding="async"
                  className="w-5 h-5 flex-shrink-0"
                />
                <span className="whitespace-nowrap">{signInText}</span>
              </button>
            </div>
          </div>

          {/* Right Column - Demo Card - Hidden on mobile */}
          <div className="hidden lg:block relative px-8 lg:px-8 lg:pr-16">
            <WatermarkDemoCard
              heading={demoCard.heading}
              headingLabel={demoCard.headingLabel}
              sampleText={{
                before: demoCard.sampleTextBefore,
                after: demoCard.sampleTextAfter,
              }}
              detectedCount={demoCard.detectedCount}
              detectedLabel={demoCard.detectedLabel}
              cleanedLabel={demoCard.cleanedLabel}
              stats={demoCard.stats}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export { WatermarkHighlight };
export default PSEOHero;
